import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Plus, Save, Trash2, Search, X, Database, MapPin, GraduationCap, ChevronDown, ChevronUp, Check, RefreshCw, CreditCard as Edit3, AlertCircle } from 'lucide-react';
import {
  personalizedCollegeService,
  type CollegeWithBranches,
  type CollegeBranch,
  type CounselingRound
} from '../lib/personalizedCollegeService';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const toInt = (v: string) => { const n = parseInt(v, 10); return isNaN(n) ? 0 : n; };

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Andaman and Nicobar Islands','Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu','Delhi','Jammu and Kashmir',
  'Ladakh','Lakshadweep','Puducherry'
];

const EMPTY_COLLEGE = {
  name: '', location: '', state: '', exam_type: 'NEET', college_type: 'Medical_Government',
  category: 'Medical', cutoff_general: 0, cutoff_obc: 0, cutoff_sc: 0, cutoff_st: 0, cutoff_ews: 0,
  fees_annual: '', seats_total: 0, nirf_ranking: 0, placement_percentage: 0,
  average_package: '', established: '', website: '',
  image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800'
};

const CATEGORIES = ['general', 'obc', 'sc', 'st', 'ews'] as const;

// Inline editable number cell — click to edit, Enter/blur to save
const CutoffCell = ({
  value,
  onSave,
  highlight = false
}: {
  value: number | null | undefined;
  onSave: (v: number) => Promise<void>;
  highlight?: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(String(value ?? 0));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocal(String(value ?? 0)); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const commit = async () => {
    const num = toInt(local);
    setEditing(false);
    if (num === (value ?? 0)) return;
    setSaving(true);
    try { await onSave(num); }
    catch { toast.error('Save failed'); setLocal(String(value ?? 0)); }
    finally { setSaving(false); }
  };

  if (saving) return <span className="badge bg-warning text-dark px-2">saving…</span>;

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setLocal(String(value ?? 0)); setEditing(false); }
        }}
        className="form-control form-control-sm text-center"
        style={{ width: 88 }}
        min="0"
      />
    );
  }

  const display = value && value > 0 ? value.toLocaleString() : '—';
  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit"
      className={`badge border ${highlight ? 'bg-primary text-white' : 'bg-light text-dark'}`}
      style={{ cursor: 'pointer', fontSize: '0.78rem', minWidth: 60, display: 'inline-block' }}
    >
      {display}
      {!highlight && <Edit3 size={8} className="ms-1 opacity-50" />}
    </span>
  );
};

// Inline text cell for round name / year
const TextCell = ({ value, onSave }: { value: string; onSave: (v: string) => Promise<void> }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocal(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const commit = async () => {
    setEditing(false);
    if (local.trim() === value) return;
    try { await onSave(local.trim()); }
    catch { toast.error('Save failed'); setLocal(value); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setLocal(value); setEditing(false); } }}
        className="form-control form-control-sm"
        style={{ width: 80 }}
      />
    );
  }
  return (
    <span onClick={() => setEditing(true)} title="Click to edit" className="text-dark" style={{ cursor: 'pointer' }}>
      {value || '—'}<Edit3 size={8} className="ms-1 opacity-40" />
    </span>
  );
};

// ─── Rounds sub-table for a single branch ───────────────────────────────────
const RoundsPanel = ({
  branch,
  collegeId,
  onRoundsChange
}: {
  branch: CollegeBranch;
  collegeId: string;
  onRoundsChange: (branchId: string, rounds: CounselingRound[]) => void;
}) => {
  const rounds = (branch.counseling_rounds || []).sort((a, b) => {
    if (a.year !== b.year) return b.year.localeCompare(a.year);
    return a.round_number - b.round_number;
  });

  const [addingRound, setAddingRound] = useState(false);
  const [newRound, setNewRound] = useState<Partial<CounselingRound>>({
    round_number: rounds.length + 1,
    round_name: `Round ${rounds.length + 1}`,
    year: new Date().getFullYear().toString(),
    cutoff_general: 0, cutoff_obc: 0, cutoff_sc: 0, cutoff_st: 0, cutoff_ews: 0, seats_filled: 0
  });
  const [saving, setSaving] = useState(false);

  const saveRoundField = async (roundId: string, field: string, value: any) => {
    await personalizedCollegeService.updateCounselingRound(roundId, { [field]: value });
    onRoundsChange(branch.id, rounds.map(r => r.id === roundId ? { ...r, [field]: value } : r));
  };

  const addRound = async () => {
    if (!newRound.round_name?.trim()) { toast.error('Round name required'); return; }
    setSaving(true);
    try {
      const created = await personalizedCollegeService.createCounselingRound({
        ...newRound as CounselingRound,
        branch_id: branch.id
      });
      onRoundsChange(branch.id, [...rounds, created]);
      setAddingRound(false);
      setNewRound({ round_number: rounds.length + 2, round_name: `Round ${rounds.length + 2}`, year: new Date().getFullYear().toString(), cutoff_general: 0, cutoff_obc: 0, cutoff_sc: 0, cutoff_st: 0, cutoff_ews: 0, seats_filled: 0 });
      toast.success('Round added');
    } catch { toast.error('Failed to add round'); }
    finally { setSaving(false); }
  };

  const deleteRound = async (roundId: string) => {
    if (!confirm('Delete this round?')) return;
    try {
      await personalizedCollegeService.deleteCounselingRound(roundId);
      onRoundsChange(branch.id, rounds.filter(r => r.id !== roundId));
      toast.success('Round deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="ms-3 mt-2 mb-1">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold small text-secondary">Counseling Rounds</span>
        <button
          onClick={() => setAddingRound(v => !v)}
          className="btn btn-outline-success btn-sm py-0 px-2 d-flex align-items-center gap-1"
          style={{ fontSize: '0.75rem' }}
        >
          {addingRound ? <X size={11} /> : <Plus size={11} />}
          {addingRound ? 'Cancel' : 'Add Round'}
        </button>
      </div>

      {/* Add round form */}
      {addingRound && (
        <div className="card border mb-2" style={{ background: '#f8fff8' }}>
          <div className="card-body p-2">
            <div className="row g-2 align-items-end">
              <div className="col">
                <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.7rem' }}>Name</label>
                <input type="text" value={newRound.round_name} onChange={e => setNewRound(p => ({ ...p, round_name: e.target.value }))} className="form-control form-control-sm" placeholder="Round 1" />
              </div>
              <div className="col-1">
                <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.7rem' }}>#</label>
                <input type="number" value={newRound.round_number} onChange={e => setNewRound(p => ({ ...p, round_number: toInt(e.target.value) }))} className="form-control form-control-sm" min="1" />
              </div>
              <div className="col-1">
                <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.7rem' }}>Year</label>
                <input type="text" value={newRound.year} onChange={e => setNewRound(p => ({ ...p, year: e.target.value }))} className="form-control form-control-sm" placeholder="2025" />
              </div>
              {CATEGORIES.map(cat => (
                <div className="col" key={cat}>
                  <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.7rem' }}>{cat.toUpperCase()}</label>
                  <input type="number" value={(newRound as any)[`cutoff_${cat}`]} onChange={e => setNewRound(p => ({ ...p, [`cutoff_${cat}`]: toInt(e.target.value) }))} className="form-control form-control-sm" min="0" placeholder="0" />
                </div>
              ))}
              <div className="col-auto">
                <button onClick={addRound} disabled={saving} className="btn btn-success btn-sm">
                  {saving ? <span className="spinner-border spinner-border-sm" /> : <Check size={13} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rounds table */}
      {rounds.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.78rem', background: '#fff' }}>
            <thead className="table-light">
              <tr>
                <th>Round</th>
                <th>Year</th>
                <th className="text-center text-success">General</th>
                <th className="text-center text-primary">OBC</th>
                <th className="text-center text-warning">SC</th>
                <th className="text-center text-danger">ST</th>
                <th className="text-center text-info">EWS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rounds.map(round => (
                <tr key={round.id}>
                  <td>
                    <TextCell value={round.round_name} onSave={v => saveRoundField(round.id, 'round_name', v)} />
                  </td>
                  <td>
                    <TextCell value={round.year} onSave={v => saveRoundField(round.id, 'year', v)} />
                  </td>
                  {CATEGORIES.map(cat => (
                    <td key={cat} className="text-center">
                      <CutoffCell
                        value={(round as any)[`cutoff_${cat}`]}
                        onSave={v => saveRoundField(round.id, `cutoff_${cat}`, v)}
                      />
                    </td>
                  ))}
                  <td>
                    <button onClick={() => deleteRound(round.id)} className="btn btn-outline-danger btn-sm py-0 px-1">
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted small mb-0 fst-italic">No rounds yet — add one above.</p>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
const PersonalizedCollegeManager = () => {
  const { isAdmin } = useAuth();
  const [colleges, setColleges] = useState<CollegeWithBranches[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [expandedCollegeId, setExpandedCollegeId] = useState<string | null>(null);
  const [expandedBranchIds, setExpandedBranchIds] = useState<Set<string>>(new Set());
  const [addingBranchFor, setAddingBranchFor] = useState<string | null>(null);
  const [newBranch, setNewBranch] = useState<Partial<CollegeBranch>>({
    branch_name: 'MBBS', branch_code: 'MBBS', cutoff_general: 0, cutoff_obc: 0,
    cutoff_sc: 0, cutoff_st: 0, cutoff_ews: 0, seats_available: 60,
    duration: '5.5 years', degree_type: 'MBBS'
  });
  const [newCollege, setNewCollege] = useState({ ...EMPTY_COLLEGE });

  useEffect(() => { if (isAdmin) fetchColleges(); }, [isAdmin, searchQuery, selectedState]);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (searchQuery) filters.search = searchQuery;
      if (selectedState !== 'all') filters.state = selectedState;
      setColleges(await personalizedCollegeService.getAll(filters));
    } catch { toast.error('Failed to fetch colleges'); }
    finally { setLoading(false); }
  };

  const saveCollegeCutoff = async (id: string, field: string, value: number) => {
    await personalizedCollegeService.update(id, { [field]: value });
    setColleges(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const saveBranchCutoff = async (branchId: string, collegeId: string, field: string, value: number) => {
    await personalizedCollegeService.updateBranch(branchId, { [field]: value });
    setColleges(prev => prev.map(c => {
      if (c.id !== collegeId) return c;
      return { ...c, branches: c.branches?.map(b => b.id === branchId ? { ...b, [field]: value } : b) };
    }));
  };

  const handleRoundsChange = (collegeId: string, branchId: string, rounds: CounselingRound[]) => {
    setColleges(prev => prev.map(c => {
      if (c.id !== collegeId) return c;
      return { ...c, branches: c.branches?.map(b => b.id === branchId ? { ...b, counseling_rounds: rounds } : b) };
    }));
  };

  const deleteCollege = async (college: CollegeWithBranches) => {
    if (!confirm(`Delete "${college.name}"?`)) return;
    try {
      await personalizedCollegeService.delete(college.id);
      setColleges(prev => prev.filter(c => c.id !== college.id));
      toast.success('College deleted');
    } catch { toast.error('Delete failed'); }
  };

  const deleteBranch = async (branchId: string, collegeId: string) => {
    if (!confirm('Delete this branch and all its rounds?')) return;
    try {
      await personalizedCollegeService.deleteBranch(branchId);
      setColleges(prev => prev.map(c =>
        c.id === collegeId ? { ...c, branches: c.branches?.filter(b => b.id !== branchId) } : c
      ));
      toast.success('Branch deleted');
    } catch { toast.error('Delete failed'); }
  };

  const addCollege = async () => {
    if (!newCollege.name.trim()) { toast.error('Name required'); return; }
    if (!newCollege.state) { toast.error('State required'); return; }
    if (!newCollege.fees_annual.trim()) { toast.error('Fees required'); return; }
    setFormSaving(true);
    try {
      await personalizedCollegeService.create(newCollege as any);
      toast.success('College added!');
      setShowAddForm(false);
      setNewCollege({ ...EMPTY_COLLEGE });
      fetchColleges();
    } catch { toast.error('Failed to add college'); }
    finally { setFormSaving(false); }
  };

  const addBranch = async (collegeId: string) => {
    if (!newBranch.branch_name?.trim()) { toast.error('Branch name required'); return; }
    setFormSaving(true);
    try {
      const created = await personalizedCollegeService.createBranch({ ...newBranch as CollegeBranch, college_id: collegeId });
      setColleges(prev => prev.map(c =>
        c.id === collegeId ? { ...c, branches: [...(c.branches || []), created] } : c
      ));
      toast.success('Branch added!');
      setAddingBranchFor(null);
    } catch { toast.error('Failed to add branch'); }
    finally { setFormSaving(false); }
  };

  const toggleBranch = (branchId: string) => {
    setExpandedBranchIds(prev => {
      const next = new Set(prev);
      next.has(branchId) ? next.delete(branchId) : next.add(branchId);
      return next;
    });
  };

  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5 text-center">
          <AlertCircle className="text-warning mb-3" size={48} />
          <h2>Admin access required</h2>
          <Link to="/" className="btn btn-primary mt-3">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container-fluid py-4 px-4">

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Database className="text-white" size={28} />
            </div>
            <div>
              <h1 className="h3 fw-bold text-dark mb-0">NEET College Cutoffs</h1>
              <p className="text-muted mb-0 small">Click any value to edit inline · Expand a college to manage branches &amp; rounds</p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button onClick={fetchColleges} className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={() => setShowAddForm(v => !v)} className="btn btn-success d-flex align-items-center gap-2">
              {showAddForm ? <X size={15} /> : <Plus size={15} />}
              {showAddForm ? 'Cancel' : 'Add College'}
            </button>
          </div>
        </div>

        {/* Add College Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="card border-0 shadow mb-4">
              <div className="card-header bg-success text-white fw-semibold">New College</div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small">Name *</label>
                    <input type="text" value={newCollege.name} onChange={e => setNewCollege(p => ({ ...p, name: e.target.value }))} className="form-control form-control-sm" placeholder="e.g., AIIMS New Delhi" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold small">City *</label>
                    <input type="text" value={newCollege.location} onChange={e => setNewCollege(p => ({ ...p, location: e.target.value }))} className="form-control form-control-sm" placeholder="New Delhi" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold small">State *</label>
                    <select value={newCollege.state} onChange={e => setNewCollege(p => ({ ...p, state: e.target.value }))} className="form-select form-select-sm">
                      <option value="">Select</option>
                      {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold small">Type</label>
                    <select value={newCollege.college_type} onChange={e => setNewCollege(p => ({ ...p, college_type: e.target.value }))} className="form-select form-select-sm">
                      <option value="Medical_Government">Govt Medical</option>
                      <option value="Medical_Private">Pvt Medical</option>
                      <option value="Government">Government</option>
                      <option value="Private">Private</option>
                    </select>
                  </div>
                  <div className="col-md-1">
                    <label className="form-label fw-semibold small">Seats</label>
                    <input type="number" value={newCollege.seats_total} onChange={e => setNewCollege(p => ({ ...p, seats_total: toInt(e.target.value) }))} className="form-control form-control-sm" min="0" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold small">Annual Fees *</label>
                    <input type="text" value={newCollege.fees_annual} onChange={e => setNewCollege(p => ({ ...p, fees_annual: e.target.value }))} className="form-control form-control-sm" placeholder="₹1,628/yr" />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label fw-semibold small">NIRF</label>
                    <input type="number" value={newCollege.nirf_ranking} onChange={e => setNewCollege(p => ({ ...p, nirf_ranking: toInt(e.target.value) }))} className="form-control form-control-sm" min="0" />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label fw-semibold small">Est.</label>
                    <input type="text" value={newCollege.established} onChange={e => setNewCollege(p => ({ ...p, established: e.target.value }))} className="form-control form-control-sm" placeholder="1956" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small">Website</label>
                    <input type="url" value={newCollege.website} onChange={e => setNewCollege(p => ({ ...p, website: e.target.value }))} className="form-control form-control-sm" placeholder="https://..." />
                  </div>

                  <div className="col-12 mt-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="fw-semibold small">College-level Cutoffs (NEET scores)</span>
                      <span className="badge bg-info text-dark small">optional — can also set at branch level</span>
                    </div>
                    <div className="row g-2">
                      {CATEGORIES.map(cat => (
                        <div className="col" key={cat}>
                          <label className="form-label mb-0 small text-uppercase fw-semibold text-muted">{cat}</label>
                          <input type="number" value={(newCollege as any)[`cutoff_${cat}`]} onChange={e => setNewCollege(p => ({ ...p, [`cutoff_${cat}`]: toInt(e.target.value) }))} className="form-control form-control-sm" min="0" placeholder="0" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="col-12 d-flex gap-2">
                    <button onClick={addCollege} disabled={formSaving} className="btn btn-success btn-sm d-flex align-items-center gap-2">
                      {formSaving ? <span className="spinner-border spinner-border-sm" /> : <Check size={14} />}
                      Save College
                    </button>
                    <button onClick={() => { setShowAddForm(false); setNewCollege({ ...EMPTY_COLLEGE }); }} className="btn btn-secondary btn-sm">Cancel</button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search & filter */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-3 px-4">
            <div className="row g-2 align-items-center">
              <div className="col-md-5">
                <div className="position-relative">
                  <Search className="position-absolute top-50 translate-middle-y text-muted" style={{ left: 12 }} size={14} />
                  <input type="text" placeholder="Search colleges…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="form-control form-control-sm ps-5" />
                </div>
              </div>
              <div className="col-md-3">
                <select value={selectedState} onChange={e => setSelectedState(e.target.value)} className="form-select form-select-sm">
                  <option value="all">All States</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col text-end text-muted small">{colleges.length} college{colleges.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
        ) : colleges.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <GraduationCap size={48} className="text-muted mb-3" />
              <h5>No colleges found</h5>
              <button onClick={() => setShowAddForm(true)} className="btn btn-primary mt-2">Add College</button>
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th style={{ minWidth: 200 }}>College</th>
                    <th style={{ minWidth: 120 }}>State / Type</th>
                    <th className="text-center" style={{ minWidth: 85 }}>General</th>
                    <th className="text-center" style={{ minWidth: 85 }}>OBC</th>
                    <th className="text-center" style={{ minWidth: 85 }}>SC</th>
                    <th className="text-center" style={{ minWidth: 85 }}>ST</th>
                    <th className="text-center" style={{ minWidth: 85 }}>EWS</th>
                    <th style={{ minWidth: 80 }}>Fees</th>
                    <th className="text-center" style={{ minWidth: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {colleges.map(college => {
                    const isExpanded = expandedCollegeId === college.id;
                    const branchCount = college.branches?.length ?? 0;
                    const roundCount = college.branches?.reduce((s, b) => s + (b.counseling_rounds?.length ?? 0), 0) ?? 0;
                    return (
                      <React.Fragment key={college.id}>
                        {/* College row */}
                        <tr className={isExpanded ? 'table-active' : ''}>
                          <td className="text-center">
                            <button
                              onClick={() => setExpandedCollegeId(isExpanded ? null : college.id)}
                              className="btn btn-sm btn-link p-0 text-muted"
                              title={isExpanded ? 'Collapse' : 'Expand branches & rounds'}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </td>
                          <td>
                            <div className="fw-semibold">{college.name}</div>
                            <div className="text-muted small d-flex align-items-center gap-1">
                              <MapPin size={10} />{college.location}
                              {branchCount > 0 && <span className="ms-2 badge bg-light text-secondary border">{branchCount} branch{branchCount !== 1 ? 'es' : ''} · {roundCount} rounds</span>}
                            </div>
                          </td>
                          <td>
                            <div className="small">{college.state}</div>
                            <span className={`badge ${college.college_type.includes('Government') ? 'bg-success' : 'bg-warning text-dark'} bg-opacity-75`} style={{ fontSize: '0.68rem' }}>
                              {college.college_type.replace('_', ' ')}
                            </span>
                          </td>
                          {CATEGORIES.map(cat => (
                            <td key={cat} className="text-center">
                              <CutoffCell
                                value={(college as any)[`cutoff_${cat}`]}
                                onSave={v => saveCollegeCutoff(college.id, `cutoff_${cat}`, v)}
                              />
                            </td>
                          ))}
                          <td className="small text-muted">{college.fees_annual}</td>
                          <td>
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                onClick={() => setExpandedCollegeId(isExpanded ? null : college.id)}
                                className={`btn btn-sm ${isExpanded ? 'btn-info' : 'btn-outline-info'}`}
                                title="Manage branches & rounds"
                              >
                                <GraduationCap size={13} />
                              </button>
                              <button onClick={() => deleteCollege(college)} className="btn btn-outline-danger btn-sm" title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded panel: branches + rounds */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={10} className="p-0">
                              <div className="bg-white border-start border-4 border-info px-4 py-3" style={{ borderLeftColor: '#0dcaf0' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="fw-bold mb-0 text-info">
                                    Branches &amp; Counseling Rounds — {college.name}
                                  </h6>
                                  <button
                                    onClick={() => setAddingBranchFor(addingBranchFor === college.id ? null : college.id)}
                                    className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                                  >
                                    {addingBranchFor === college.id ? <X size={12} /> : <Plus size={12} />}
                                    {addingBranchFor === college.id ? 'Cancel' : 'Add Branch'}
                                  </button>
                                </div>

                                {/* Add branch form */}
                                {addingBranchFor === college.id && (
                                  <div className="card border mb-3">
                                    <div className="card-body p-3">
                                      <div className="row g-2 align-items-end">
                                        <div className="col-md-3">
                                          <label className="form-label fw-semibold small mb-1">Branch Name *</label>
                                          <input type="text" value={newBranch.branch_name} onChange={e => setNewBranch(p => ({ ...p, branch_name: e.target.value }))} className="form-control form-control-sm" placeholder="MBBS" />
                                        </div>
                                        <div className="col-md-1">
                                          <label className="form-label fw-semibold small mb-1">Code</label>
                                          <input type="text" value={newBranch.branch_code} onChange={e => setNewBranch(p => ({ ...p, branch_code: e.target.value }))} className="form-control form-control-sm" placeholder="MBBS" />
                                        </div>
                                        <div className="col-md-1">
                                          <label className="form-label fw-semibold small mb-1">Seats</label>
                                          <input type="number" value={newBranch.seats_available} onChange={e => setNewBranch(p => ({ ...p, seats_available: toInt(e.target.value) }))} className="form-control form-control-sm" min="0" />
                                        </div>
                                        {CATEGORIES.map(cat => (
                                          <div className="col" key={cat}>
                                            <label className="form-label fw-semibold small mb-1 text-uppercase">{cat}</label>
                                            <input type="number" value={(newBranch as any)[`cutoff_${cat}`]} onChange={e => setNewBranch(p => ({ ...p, [`cutoff_${cat}`]: toInt(e.target.value) }))} className="form-control form-control-sm" min="0" />
                                          </div>
                                        ))}
                                        <div className="col-auto">
                                          <button onClick={() => addBranch(college.id)} disabled={formSaving} className="btn btn-success btn-sm">
                                            {formSaving ? <span className="spinner-border spinner-border-sm" /> : <Save size={13} />}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Branches list */}
                                {college.branches && college.branches.length > 0 ? (
                                  <div className="vstack gap-2">
                                    {college.branches.map(branch => {
                                      const branchExpanded = expandedBranchIds.has(branch.id);
                                      const rCount = branch.counseling_rounds?.length ?? 0;
                                      return (
                                        <div key={branch.id} className="card border">
                                          {/* Branch header row */}
                                          <div className="card-body p-0">
                                            <div className="table-responsive">
                                              <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                                                <thead className="table-secondary">
                                                  <tr>
                                                    <th style={{ width: 28 }}></th>
                                                    <th>Branch</th>
                                                    <th className="text-center">Seats</th>
                                                    <th className="text-center text-success">General</th>
                                                    <th className="text-center text-primary">OBC</th>
                                                    <th className="text-center text-warning">SC</th>
                                                    <th className="text-center text-danger">ST</th>
                                                    <th className="text-center text-info">EWS</th>
                                                    <th className="text-center">Rounds</th>
                                                    <th></th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  <tr>
                                                    <td className="text-center">
                                                      <button
                                                        onClick={() => toggleBranch(branch.id)}
                                                        className="btn btn-sm btn-link p-0 text-muted"
                                                        title={branchExpanded ? 'Hide rounds' : 'Show rounds'}
                                                      >
                                                        {branchExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                      </button>
                                                    </td>
                                                    <td>
                                                      <span className="fw-semibold">{branch.branch_name}</span>
                                                      {branch.branch_code && <span className="badge bg-secondary ms-1">{branch.branch_code}</span>}
                                                    </td>
                                                    <td className="text-center text-muted">{branch.seats_available || '—'}</td>
                                                    {CATEGORIES.map(cat => (
                                                      <td key={cat} className="text-center">
                                                        <CutoffCell
                                                          value={(branch as any)[`cutoff_${cat}`]}
                                                          onSave={v => saveBranchCutoff(branch.id, college.id, `cutoff_${cat}`, v)}
                                                        />
                                                      </td>
                                                    ))}
                                                    <td className="text-center">
                                                      <button onClick={() => toggleBranch(branch.id)} className={`btn btn-sm ${branchExpanded ? 'btn-primary' : 'btn-outline-primary'} py-0 px-2`} style={{ fontSize: '0.72rem' }}>
                                                        {rCount} round{rCount !== 1 ? 's' : ''}
                                                      </button>
                                                    </td>
                                                    <td>
                                                      <button onClick={() => deleteBranch(branch.id, college.id)} className="btn btn-outline-danger btn-sm py-0 px-1">
                                                        <Trash2 size={12} />
                                                      </button>
                                                    </td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>

                                            {/* Rounds panel — shown when branch is expanded */}
                                            {branchExpanded && (
                                              <div className="border-top px-3 py-2 bg-light">
                                                <RoundsPanel
                                                  branch={branch}
                                                  collegeId={college.id}
                                                  onRoundsChange={(branchId, rounds) => handleRoundsChange(college.id, branchId, rounds)}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-muted small fst-italic mb-0">No branches yet.</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-muted small mt-3">
          <strong>Tip:</strong> Click <ChevronDown size={12} className="mx-1" /> to expand a college · click cutoff numbers to edit inline · click the rounds button to expand round history.
        </p>
      </div>
    </div>
  );
};

export default PersonalizedCollegeManager;
